/**
 * Verbose audio logging, off by default. Turned on by the "Debug audio" setting, `?tonist-debug` in the URL, or
 * `localStorage['tonist.debug'] = '1'`. Every line is prefixed with the category and the AudioContext time so a
 * sequence of pointer → voice → param events can be followed in the console.
 *
 *   [tonist:voice 12.345s] #7 noteOn sa hz=138.6 kinds=[sample×2] attack=0.40
 */
export type LogCategory = 'context' | 'load' | 'voice' | 'pitch' | 'gesture' | 'fx' | 'pointer' | 'watchdog' | 'store';

let enabled = false;
let clock: (() => number) | null = null;
const rate = new Map<string, { t: number; n: number }>();

const COLORS: Record<LogCategory, string> = {
  context: '#5B7BD6', load: '#3EB489', voice: '#FFE08A', pitch: '#E8B830', gesture: '#D4AF37', fx: '#B3261E',
  pointer: '#CFC4B4', watchdog: '#E34234', store: '#8E8578',
};

export function setAudioDebug(on: boolean): void {
  if (on === enabled) return;
  enabled = on;
  if (on) console.info('%c[tonist] audio debug logging ON', 'color:#FFE08A;font-weight:bold');
}
export const audioDebugEnabled = (): boolean => enabled;
/** The engine installs the AudioContext clock so lines carry context time. */
export function setLogClock(fn: (() => number) | null): void { clock = fn; }

/** Should this session log at all? Query flag, localStorage flag, or the persisted setting (read by the caller). */
export function debugRequestedByEnvironment(): boolean {
  try {
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('tonist-debug')) return true;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('tonist.debug') === '1') return true;
  } catch { /* storage blocked */ }
  return false;
}

function stamp(cat: LogCategory): string {
  const t = clock ? clock() : performance.now() / 1000;
  return `[tonist:${cat} ${t.toFixed(3)}s]`;
}

export const audioLog = {
  /** Cheap when disabled: callers may pass a thunk for expensive detail. */
  log(cat: LogCategory, message: string | (() => string), data?: unknown): void {
    if (!enabled) return;
    const msg = typeof message === 'function' ? message() : message;
    if (data === undefined) console.log(`%c${stamp(cat)}%c ${msg}`, `color:${COLORS[cat]}`, 'color:inherit');
    else console.log(`%c${stamp(cat)}%c ${msg}`, `color:${COLORS[cat]}`, 'color:inherit', data);
  },
  warn(cat: LogCategory, message: string, data?: unknown): void {
    if (!enabled) return;
    if (data === undefined) console.warn(`${stamp(cat)} ${message}`); else console.warn(`${stamp(cat)} ${message}`, data);
  },
  /** Always printed (even with debug off): things that must never happen silently. */
  error(cat: LogCategory, message: string, data?: unknown): void {
    if (data === undefined) console.error(`${stamp(cat)} ${message}`); else console.error(`${stamp(cat)} ${message}`, data);
  },
  /** Rate-limited variant for per-frame streams (gesture, pitch glides): at most `perSec` lines per key. */
  throttled(cat: LogCategory, key: string, perSec: number, message: string | (() => string)): void {
    if (!enabled) return;
    const now = performance.now();
    const r = rate.get(key);
    if (r && now - r.t < 1000 / perSec) return;
    rate.set(key, { t: now, n: (r?.n ?? 0) + 1 });
    audioLog.log(cat, message);
  },
};

export const fmtHz = (hz: number): string => `${hz.toFixed(1)}Hz`;
export const fmt = (n: number, d = 2): string => n.toFixed(d);

// ---------------------------------------------------------------- remote sink (debug mode)
/**
 * In debug mode every logged line — the audio log, console output, uncaught errors — is also batched to
 * POST /api/debug/log so the server can keep a file for later analysis. Batches flush every 2 s or at 200 lines,
 * and on pagehide via sendBeacon. Lines are trimmed to 2 KB; nothing is sent while the sink is off.
 */
interface RemoteLine { t: number; level: string; msg: string }
const SESSION = Math.random().toString(36).slice(2, 10);
let remoteOn = false;
let buffer: RemoteLine[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let consolePatched = false;
const original: Partial<Record<'log' | 'info' | 'warn' | 'error' | 'debug', (...a: unknown[]) => void>> = {};

function fmtArg(a: unknown): string {
  if (typeof a === 'string') return a;
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  try { return JSON.stringify(a); } catch { return String(a); }
}

function push(level: string, msg: string): void {
  if (!remoteOn) return;
  buffer.push({ t: Date.now(), level, msg: msg.length > 2048 ? msg.slice(0, 2048) + '…' : msg });
  if (buffer.length >= 200) void flushRemote();
  else if (!flushTimer) flushTimer = setTimeout(() => { flushTimer = null; void flushRemote(); }, 2000);
}

async function flushRemote(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  const lines = buffer; buffer = [];
  const body = JSON.stringify({ session: SESSION, ua: navigator.userAgent, url: location.pathname, lines });
  try {
    if (useBeacon && navigator.sendBeacon) { navigator.sendBeacon('/api/debug/log', new Blob([body], { type: 'application/json' })); return; }
    await fetch('/api/debug/log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'same-origin', keepalive: true });
  } catch { /* the server may be absent (static preview); drop the batch */ }
}

/** Turn the remote sink on/off. Patches console methods once so plain console output is captured too. */
export function setRemoteLogging(on: boolean): void {
  if (on === remoteOn) return;
  remoteOn = on;
  if (on) {
    if (!consolePatched) {
      consolePatched = true;
      for (const level of ['log', 'info', 'warn', 'error', 'debug'] as const) {
        const orig = console[level].bind(console);
        original[level] = orig;
        console[level] = (...args: unknown[]) => {
          orig(...args);
          // strip %c formatting directives and their style arguments
          const [first, ...rest] = args;
          if (typeof first === 'string' && first.includes('%c')) {
            const styles = (first.match(/%c/g) ?? []).length;
            push(level, first.replace(/%c/g, '') + rest.slice(styles).map((a) => ' ' + fmtArg(a)).join(''));
          } else push(level, args.map(fmtArg).join(' '));
        };
      }
      window.addEventListener('error', (e) => push('error', `uncaught: ${e.message} @ ${e.filename}:${e.lineno}`));
      window.addEventListener('unhandledrejection', (e) => push('error', `unhandled rejection: ${fmtArg(e.reason)}`));
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushRemote(true); });
      window.addEventListener('pagehide', () => void flushRemote(true));
    }
    push('info', `[tonist] remote logging session ${SESSION} started (${navigator.userAgent})`);
    void flushRemote();
  } else {
    void flushRemote();
  }
}
export const remoteLogSession = (): string => SESSION;
